import React, { useState } from "react";
import ImageIcon from "@mui/icons-material/Image";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const FileUpload = ({
  label = "Upload File",
  onFileUrlChange,
  document_id,
  base64Image,
}) => {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        resolve(`data:${file.type};base64,${base64String}`);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    const fileUrl = URL.createObjectURL(uploadedFile);
    setFile(uploadedFile);
    setFileUrl(fileUrl);
    setLoading(true);
  
    try {
      const base64Image = await convertFileToBase64(uploadedFile);
      setTimeout(() => {
        onFileUrlChange &&
          onFileUrlChange({ document_id, fileUrl, base64Image });
        setLoading(false);
        setOpenModal(true);
        setSaved(false); // Reset saved state when a new file is uploaded
  
        // Reset the file input to allow re-upload of the same file
        event.target.value = null;
      }, 500);
    } catch (error) {
      console.error("Error converting file to Base64:", error);
    }
  };

  const handleCloseModal = () => {
    if (!saved) {
      // If the modal is closed without saving, reset the file
      setFile(null);
      setFileUrl(null);
    }
    setOpenModal(false);
  };

  const handleSave = () => {
    setSaved(true);  // Set saved to true when the save button is clicked
    setOpenModal(false);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = base64Image;
    link.download = "downloaded_image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(
      `<img src="${base64Image}" style="max-width: 100%; max-height: 100%;" />`
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const renderFilePreview = () => {
    if (file) {
      const url = fileUrl || URL.createObjectURL(file);
      if (file.type.startsWith("image/")) {
        return (
          <>
            <img
              src={url}
              alt={file.name}
              style={{ maxWidth: "100%", maxHeight: "100%", marginBottom: "15px" }}
            />
            <br />
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </>
        );
      }

      if (file.type === "application/pdf") {
        return (
          <>
            <iframe
              src={url}
              title={file.name}
              style={{ width: "100%", height: "500px", border: "none" }}
            />
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </>
        );
      }

      return (
        <Typography>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {file.name}
          </a>
        </Typography>
      );
    }

    if (base64Image) {
      if (base64Image.startsWith("data:image/")) {
        return (
          <>
            <img
              src={base64Image}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "100%", marginBottom: "15px" }}
            />
            <br />
            <Button variant="contained" onClick={handlePrint}>
              Print
            </Button>
            <Button variant="contained" sx={{ mx: 2 }} onClick={handleDownload}>
              Download
            </Button>
          </>
        );
      } else if (base64Image.startsWith("data:application/pdf")) {
        return (
          <iframe
            src={base64Image}
            title="Preview"
            style={{ width: "100%", height: "500px", border: "none" }}
          />
        );
      } else {
        return (
          <Typography>
            <a href={base64Image} target="_blank" rel="noopener noreferrer">
              View File
            </a>
          </Typography>
        );
      }
    }

    return null;
  };

  return (
    <div>
      {loading && (
        <div className="loading-overlay">Loading, please wait...</div>
      )}
      <Typography>
        {typeof label === "string" ? label : "Upload File"}
        {(file || base64Image || saved) && (
          <Button
            onClick={() => setOpenModal(true)}
            style={{ marginLeft: "4px", minWidth: 0, padding: 0 }}
          >
            <ImageIcon style={{ width: "20px" }} />
          </Button>
        )}
        <Button
          style={{
            backgroundColor: "#fff",
            marginLeft: "10px",
            color: "#1976d2",
            padding: 0,
            minWidth: 0,
            boxShadow: "none",
          }}
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon style={{ width: "18px" }} />}
          onClick={() => setLoading(false)}
        >
          <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
        </Button>
      </Typography>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="file-modal-title"
        aria-describedby="file-modal-description"
      >
        <div
          style={{
            position: "absolute",
            padding: "20px",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxHeight: "100%",
            background: "#fff",
            borderRadius: "5px",
            boxShadow: 24,
            overflow: "auto",
            textAlign: 'center'
          }}
        >
          <Typography style={{ textAlign: "right" }}>
            <CloseIcon style={{ cursor: "pointer" }} onClick={handleCloseModal} />
          </Typography>
          <Typography variant="body2" id="file-modal-description">
            {renderFilePreview()}
          </Typography>
        </div>
      </Modal>
    </div>
  );
};

export default FileUpload;
